import { useEffect, useRef, useState } from 'react';
import { useAuth, useSignIn, useUser } from '@clerk/clerk-react';
import { QuickstartTheme } from '../types/theme';
import { QuickstartCard } from './QuickstartCard';
import { getPersistentPlayerId } from '../../../api';
import { useIsMobileDevice } from '../../../utility/utility';
import { useIsLandscape } from '../utils/responsiveHooks';

interface QuickstartCustomAuthProps {
  theme: QuickstartTheme;
  authProvider: string;
  onAuthSuccess: (playerAuthId: string, email?: string, persistentId?: string) => void;
  onAuthFailure: (reason: string) => void;
  onClerkUnavailable?: () => void;
}

// Social provider config
const socialProviders = [
  { name: 'twitch', icon: '/quickstart/socials/twitch.svg', strategy: 'oauth_twitch' },
  { name: 'discord', icon: '/quickstart/socials/discord.svg', strategy: 'oauth_discord' },
  { name: 'tiktok', icon: '/quickstart/socials/tiktok.svg', strategy: 'oauth_tiktok' },
  { name: 'facebook', icon: '/quickstart/socials/facebook.svg', strategy: 'oauth_facebook' },
  { name: 'google', icon: '/quickstart/socials/google.svg', strategy: 'oauth_google' },
  { name: 'linkedin', icon: '/quickstart/socials/linkedin.svg', strategy: 'oauth_linkedin_oidc' },
  { name: 'microsoft', icon: '/quickstart/socials/microsoft.svg', strategy: 'oauth_microsoft' },
];

export const QuickstartCustomAuth = ({
  theme,
  authProvider,
  onAuthSuccess,
  onAuthFailure,
  onClerkUnavailable,
}: QuickstartCustomAuthProps) => {
  const auth = useAuth();
  const { isSignedIn, userId } = auth;
  const { user } = useUser();
  const { signIn } = useSignIn();
  const hasCalledSuccessRef = useRef(false);
  const wasSignedInOnMountRef = useRef(isSignedIn);

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMobile = useIsMobileDevice();
  const isLandscape = useIsLandscape();
  const isMobileLandscape = isMobile && isLandscape;

  // Handle OAuth sign-in
  const handleSocialSignIn = async (strategy: string) => {
    if (!signIn) return;

    try {
      setIsAuthenticating(true);
      setError(null);
      console.log('[QuickstartCustomAuth] Starting OAuth for:', strategy);

      await signIn.authenticateWithRedirect({
        strategy: strategy as
          | 'oauth_discord'
          | 'oauth_facebook'
          | 'oauth_google'
          | 'oauth_tiktok'
          | 'oauth_twitch',
        redirectUrl: window.location.href,
        redirectUrlComplete: window.location.href,
      });
    } catch (err) {
      console.error('[QuickstartCustomAuth] OAuth error:', err);
      setError('Authentication failed. Please try again.');
      setIsAuthenticating(false);
      const errorMessage = err instanceof Error ? err.message : 'oauth_error';
      onAuthFailure(errorMessage);
    }
  };

  // Mark localStorage with auth_pending
  useEffect(() => {
    if (!isSignedIn) {
      const timestamp = Date.now().toString();
      localStorage.setItem('quickstart_auth_pending', timestamp);
      console.log('[QuickstartCustomAuth] Set localStorage auth_pending with timestamp:', timestamp);
    }
  }, [isSignedIn]);

  // Detect when auth succeeds
  useEffect(() => {
    console.log('[QuickstartCustomAuth] Auth state changed:', { isSignedIn, userId, hasCalledSuccess: hasCalledSuccessRef.current });

    if (isSignedIn && userId && !hasCalledSuccessRef.current) {
      hasCalledSuccessRef.current = true;

      // Clear auth-related localStorage flag
      localStorage.removeItem('quickstart_auth_pending');
      console.log('[QuickstartCustomAuth] Cleared localStorage auth_pending flag');

      // Clear auth-related URL parameters
      const url = new URL(window.location.href);
      let paramsChanged = false;
      if (url.searchParams.has('auth_pending')) {
        url.searchParams.delete('auth_pending');
        paramsChanged = true;
      }
      if (url.searchParams.has('auth_success')) {
        url.searchParams.delete('auth_success');
        paramsChanged = true;
      }
      if (paramsChanged) {
        window.history.replaceState({}, '', url.toString());
        console.log('[QuickstartCustomAuth] Cleared auth parameters from URL');
      }

      // Get email and persistentId
      const email = user?.primaryEmailAddress?.emailAddress;
      const persistentId = getPersistentPlayerId();

      console.log('[QuickstartCustomAuth] Auth data:', { userId, email, persistentId });

      if (wasSignedInOnMountRef.current) {
        console.log('[QuickstartCustomAuth] User already signed in on mount, proceeding directly');
        onAuthSuccess(userId, email, persistentId);
        return;
      }

      console.log('[QuickstartCustomAuth] Auth succeeded, calling onAuthSuccess...');
      onAuthSuccess(userId, email, persistentId);
    }
  }, [isSignedIn, userId, user, onAuthSuccess]);

  // Show loading state if already signed in or authenticating
  if (isAuthenticating || (isSignedIn && userId)) {
    return (
      <div
        className='flex min-h-screen items-center justify-center overflow-auto p-4'
        style={{
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <QuickstartCard theme={theme}>
          <div className='flex flex-col items-center text-center'>
            <div className='mb-4'>
              <div
                className='mx-auto h-12 w-12 animate-spin rounded-full border-4 border-t-transparent'
                style={{
                  borderColor: `${theme.spinnerColor || theme.primaryColor}40`,
                  borderTopColor: theme.spinnerColor || theme.primaryColor,
                }}
              />
            </div>
            <p className='text-xl font-semibold text-white'>
              {isSignedIn ? 'Already signed in' : 'Authenticating...'}
            </p>
            <p className='mt-2 text-sm text-gray-400'>{isSignedIn ? 'Redirecting...' : 'Please wait...'}</p>
          </div>
        </QuickstartCard>
      </div>
    );
  }

  return (
    <div
      className='flex min-h-screen items-center justify-center overflow-auto p-4'
      style={{
        backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <QuickstartCard theme={theme} showHeader={false}>
        <div className={`flex flex-col items-center text-center ${isMobileLandscape ? 'px-4 py-3' : 'px-6 py-4'}`}>
          {/* "THANKS FOR PLAYING" header */}
          <h1
            className={`font-["Inter"] font-bold tracking-wide ${isMobileLandscape ? 'text-2xl mb-1' : 'mb-2 text-3xl'}`}
            style={{ color: theme.primaryColor }}
          >
            THANKS FOR PLAYING
          </h1>

          {/* "CONNECT WITH US!" subheader */}
          <h2 className={`font-["Inter"] font-semibold text-white ${isMobileLandscape ? 'text-xl mb-4' : 'mb-6 text-2xl'}`}>
            CONNECT WITH US!
          </h2>

          {/* Social Login Buttons */}
          <div className={`flex flex-wrap justify-center mx-auto ${isMobileLandscape ? 'gap-3 max-w-[260px] mb-4' : 'mb-6 gap-4 max-w-[280px]'}`}>
            {socialProviders.map((provider) => (
              <button
                key={provider.name}
                onClick={() => handleSocialSignIn(provider.strategy)}
                className={`group flex items-center justify-center rounded-full transition-all hover:scale-110 ${isMobileLandscape ? 'h-12 w-12' : 'h-14 w-14'}`}
                style={{
                  backgroundColor: theme.modalBackgroundColor || '#1a1a1a',
                  border: `2px solid ${theme.primaryColor}`,
                }}
                aria-label={`Sign in with ${provider.name}`}
              >
                <img
                  src={provider.icon}
                  alt={provider.name}
                  className={isMobileLandscape ? 'h-7 w-7' : 'h-8 w-8'}
                />
              </button>
            ))}
          </div>

          {/* Error Message */}
          {error && <p className='mb-4 text-sm text-red-400'>{error}</p>}

          {/* Studio Logo */}
          {theme.studioLogo && (
            <div className='mb-4'>
              <img src={theme.studioLogo} alt={theme.studioName} className='h-12' />
            </div>
          )}

          {/* Game Logo */}
          {theme.gameLogo && (
            <div className='mb-6'>
              <img src={theme.gameLogo} alt={theme.gameName} className='h-20' />
            </div>
          )}

          {/* Powered by Playcast */}
          <img src='/quickstart/pbp.png' alt='Powered by Playcast' className='h-6 opacity-80' />
        </div>
      </QuickstartCard>
    </div>
  );
};
